import { SpreadsheetColumnsService } from './spreadsheet-columns.service';
import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { APIService, KPI } from '../api';
import { AssetsService } from './assets.service';
import { DocumentTypesService } from './document-types.service';
import { InitiativesService } from './initiatives.service';
import { InvestmentCategoriesService } from './investment-categories.service';
import { InvestmentReasonsService } from './investment-reasons.service';
import { PerimetersService } from './perimeters.service';
import { ReferenceDataService } from './reference-data.service';
import { idMapToArray, ScopeService } from './scope.service';
import { YearsService } from "./years.service";
import { ActivatedRoute } from "@angular/router";
import { InvestmentScenariiService } from "./investment-scenarii.service";
import { RoadmapService } from './roadmap.service';
import { QuotePartService } from './quote-part.service';
import { ConfigService } from './config.service';

@Injectable()
export class SpreadsheetColumnsAutoPpaService extends SpreadsheetColumnsService {

  constructor(
    protected documentTypesService: DocumentTypesService,
    protected investmentCategoriesService: InvestmentCategoriesService,
    protected investmentReasonsService: InvestmentReasonsService,
    protected scope: ScopeService,
    protected translate: TranslateService,
    protected api: APIService,
    protected perimetersService: PerimetersService,
    protected referenceDataService: ReferenceDataService,
    protected assetsService: AssetsService,
    protected yearsService: YearsService,
    protected route: ActivatedRoute,
    protected investmentScenariiService: InvestmentScenariiService,
    protected initiativesService: InitiativesService,
    protected roadmapService: RoadmapService,
    protected quotePartService: QuotePartService,
    protected config: ConfigService
  ) {
    super(
      documentTypesService, investmentCategoriesService, investmentReasonsService,
      scope, translate, api, perimetersService, referenceDataService, assetsService,
      yearsService, route, investmentScenariiService, initiativesService, roadmapService,
      quotePartService, config
    );
  }

  /**
   * SIMPLE AUTO PPA COLUMNS
   */
  private agGridAutoPpaColumns(): KPI.CustomColDef[] {
    return [
      {
        field: 'label',
        headerName: this.translate.instant('core.utils.spreadsheet_columns.column_label'),
        headerCheckboxSelection: true,
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        width: 350,
      },
      {
        field: 'site',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_site',
        ),
        width: 160,
        sort: 'asc',
        refData: this.sitesMap,
      },
      {
        field: 'perimeter',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_perimeter',
        ),
        width: 220,
        refData: this.perimetersMap,
      },
      {
        field: 'budget',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_budget',
        ),
        valueFormatter: this.priceFormatter,
        filter: 'agNumberColumnFilter',
        width: 90,
      },
      {
        field: 'year',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_year',
        ),
        width: 80,
      },
      {
        field: 'priority',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_priority',
        ),
        width: 80,
        refData: this.prioritiesMap,
      },
      {
        field: 'status',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_investment_status',
        ),
        width: 110,
        refData: this.investmentStatusesMap,
      },
      {
        field: 'asset_name',
        headerName: this.translate.instant(
          'core.utils.spreadsheet_columns.column_asset_label',
        ),
        width: 230,
      },
      {
        field: 'replacement_asset_type',
        headerName: this.translate.instant('Replacement asset type'),
        width: 230,
        refData: this.assetTypesMap,
      },
    ];
  }

  public getAgGridAutoPpaColumns(): Observable<KPI.CustomColDef[]> {
    return combineLatest([
      this.perimetersService.allPerimeters$,
      this.scope.investmentStatuses$.pipe(first(), idMapToArray()),
      this.scope.investmentPriorities$.pipe(idMapToArray()),
      this.scope.assetTypes$.pipe(idMapToArray()),
    ]).pipe(
      map(([perimeters, statuses, priorities, assetTypes]) => {
        /**
         * Setup reference data
         */
        perimeters.forEach((perimeter) => {
          this.sitesMap[perimeter.id] = perimeter.name;
          perimeter.sub_perimeters.forEach((sub_perimeter) => {
            this.perimetersMap[sub_perimeter.id] =
              perimeter.name + ' - ' + sub_perimeter.name;
            this.perimeters[sub_perimeter.id] = sub_perimeter.name;
          });
        });

        statuses.forEach((investmentStatus) => {
          this.investmentStatusesMap[investmentStatus.id] =
            investmentStatus.name;
        });

        priorities.forEach((priority) => {
          this.prioritiesMap[priority.id] = priority.name.toString();
        });

        assetTypes.forEach((assetType) => {
          this.assetTypesMap[assetType.id] =
            assetType.name;
        });

        return this.agGridAutoPpaColumns();
      },
      ),
    );
  }

}
